import React, { useState } from "react";
import styled from "styled-components";
import { LuArrowUpRight, LuPlay } from "react-icons/lu";

import brk from "../../util/breakpoints";
import Modal, { Dismiss } from "../Modal";
import StatusChip from "../StatusChip";

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  h2 {
    color: var(--text);
    font-family: var(--fontHeading);
    font-size: 36px;
    font-weight: 700;
    line-height: 1.08;
    @media ${brk.device.tablet} {
      font-size: 26px;
    }
  }
  p {
    color: var(--textMuted);
    font-family: var(--fontBody);
    font-size: 16px;
    line-height: 1.5;
  }
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  span {
    color: var(--textDim);
    font-family: var(--fontMono);
    font-size: 9px;
  }
`;

const SiteLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--lime);
  font-family: var(--fontMono);
  font-size: 11px;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Viewer = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: var(--bg);
  overflow: hidden;
  img,
  iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: none;
  }
  img {
    object-fit: contain;
  }
`;

const ViewerCaption = styled.p`
  margin-top: -14px;
  color: var(--textDim);
  font-family: var(--fontMono);
  font-size: 10px;
  text-transform: uppercase;
`;

const Thumbnails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const Thumbnail = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 92px;
  height: 58px;
  border: 1px solid var(--border);
  border-radius: 8px;
  outline: 2px solid ${(props) => (props.$selected ? "var(--purple)" : "transparent")};
  outline-offset: -2px;
  background-color: var(--card);
  background-image: url(${(props) => props.$img});
  background-position: center;
  background-size: cover;
  color: var(--text);
  opacity: ${(props) => (props.$selected ? 1 : 0.6)};
  cursor: pointer;
  transition: opacity 0.2s, outline-color 0.2s;
  &:hover {
    opacity: 1;
  }
  svg {
    padding: 5px;
    border-radius: 50%;
    background: rgba(8, 10, 16, 0.72);
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 240px;
  gap: 40px;
  padding-top: 26px;
  border-top: 1px solid var(--border);
  @media ${brk.device.tablet} {
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
    padding-top: 20px;
  }
`;

const Story = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  h3 {
    color: var(--textDim);
    font-family: var(--fontMono);
    font-size: 10px;
    font-weight: 400;
    text-transform: uppercase;
  }
  p {
    color: var(--textMuted);
    font-family: var(--fontBody);
    font-size: 15px;
    line-height: 1.6;
  }
`;

const Tools = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  span {
    padding: 6px 10px;
    border: 1px solid var(--border);
    border-radius: 999px;
    color: var(--textMuted);
    font-family: var(--fontMono);
    font-size: 10px;
    white-space: nowrap;
  }
`;

// A work item counts as having detail when there's anything beyond what the card already shows.
export const hasDetail = (item) => {
  return Boolean(
    item.questText ||
      item.lootText ||
      item.toolsUsed.length ||
      item.contentData.length
  );
};

function ProjectDetail(props) {
  const { item, onClose } = props;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedContent = item.contentData[selectedIndex];

  return (
    <Modal onClose={onClose} labelledBy="project-title" maxWidth={920}>
      <Header>
        <HeaderCopy>
          <Meta>
            <StatusChip $variant={item.isSoftware ? "lime" : "purple"}>
              {item.tag}
            </StatusChip>
            <span>{item.code}</span>
          </Meta>
          <h2 id="project-title">{item.title}</h2>
          <p>{item.shortDesc}</p>
          {item.url && (
            <SiteLink
              href={`https://${item.url}/`}
              target={"_blank"}
              rel={"noreferrer"}
            >
              {item.url}
              <LuArrowUpRight size={13} />
            </SiteLink>
          )}
        </HeaderCopy>
        <Dismiss onClick={onClose} />
      </Header>
      {selectedContent && (
        <>
          <Viewer>
            {selectedContent.isVideo ? (
              <iframe
                src={selectedContent.videoUrl}
                title={selectedContent.title}
                allowFullScreen
              />
            ) : (
              <img alt={selectedContent.title} src={selectedContent.img} />
            )}
          </Viewer>
          <ViewerCaption>{selectedContent.title}</ViewerCaption>
          {item.contentData.length > 1 && (
            <Thumbnails>
              {item.contentData.map((content, index) => {
                return (
                  <Thumbnail
                    key={index}
                    $img={content.img}
                    $selected={index === selectedIndex}
                    aria-label={content.title}
                    onClick={() => {
                      setSelectedIndex(index);
                    }}
                  >
                    {content.isVideo && <LuPlay size={26} />}
                  </Thumbnail>
                );
              })}
            </Thumbnails>
          )}
        </>
      )}
      <Body>
        <Story>
          {item.questText && (
            <Section>
              <h3>Overview</h3>
              <p>{item.questText}</p>
            </Section>
          )}
          {item.lootText && (
            <Section>
              <h3>What I took away</h3>
              <p>{item.lootText}</p>
            </Section>
          )}
        </Story>
        {item.toolsUsed.length > 0 && (
          <Section>
            <h3>Built with</h3>
            <Tools>
              {item.toolsUsed.map((tool) => {
                return <span key={tool}>{tool}</span>;
              })}
            </Tools>
          </Section>
        )}
      </Body>
    </Modal>
  );
}

export default ProjectDetail;
